import React from "react";
import { Dialog, DialogContent, DialogActions, Typography, Avatar, Box, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { SlCallIn } from "react-icons/sl";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { LuScreenShareOff, LuScreenShare } from "react-icons/lu";

// Define the pulse animation
const pulseLight = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
    opacity: 0;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
    opacity: 0;
  }
`;

// Styled components
const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: 'rgba(17, 25, 40, 0.9)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255, 255, 255, 0.125)',
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  }
}));

const PulsingContainer = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
    animation: `${pulseLight} 3s infinite`,
    width: '200%',
    height: '200%',
  }
});

const CallButton = styled(IconButton)(({ theme, callaction }) => ({
  backgroundColor: callaction === 'accept' ? 'rgba(46, 213, 115, 0.9)' : 'rgba(255, 71, 87, 0.9)',
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: callaction === 'accept' ? 'rgba(46, 213, 115, 1)' : 'rgba(255, 71, 87, 1)',
    transform: 'scale(1.1)',
  },
  padding: theme.spacing(2.5),
  margin: theme.spacing(1),
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const ModernAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: '3px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const CallNotification = ({
  open,
  onClose,
  acceptCall,
  endCall,
  callTypeRef,
  toggleScreenShare,
  isScreenSharing,
  message,
}) => {
  return (
    <ModernDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <PulsingContainer>
        <DialogContent>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            gap={3}
            sx={{ py: 2 }}
          >
            <ModernAvatar
              src="/default-avatar.png"
              alt="Caller"
            />
            <Typography 
              variant="h5" 
              component="h2"
              sx={{ 
                color: 'white',
                fontWeight: 600,
                textAlign: 'center'
              }}
            >
              {message}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.4)',
                textAlign: 'center'
              }}
            >
              {new Date().toLocaleTimeString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            justifyContent: 'center', 
            pb: 4,
            gap: 2 
          }}
        >
          {callTypeRef?.current === "video" && (
            <CallButton
              onClick={toggleScreenShare}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              {isScreenSharing ? <LuScreenShareOff /> : <LuScreenShare />}
            </CallButton>
          )}
          <CallButton
            callaction="accept"
            onClick={acceptCall}
            aria-label="accept call"
          >
            <SlCallIn style={{ fontSize: 32 }} />
          </CallButton>
          <CallButton
            callaction="reject"
            onClick={endCall}
            aria-label="reject call"
          >
            <HiOutlinePhoneMissedCall style={{ fontSize: 32 }} />
          </CallButton>
        </DialogActions>
      </PulsingContainer>
    </ModernDialog>
  );
};

export default CallNotification;